import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { RootState } from 'store';
import apiClient from 'utils/apiClient';

export interface AccountSearchParams {
    page?: number;
    perPage?: number;
    totalSearchKeyword?: string;
    groupIds?: string[];
    uuid?: string;
    uuidSearchType?: string;
    name?: string;
    nameSearchType?: string;
    email?: string;
    emailSearchType?: string;
    status?: string[];
    language?: string[];
    funnel?: string[];
    createdStartDateTime?: string;
    createdEndDateTime?: string;
    lastSeenStartDateTime?: string;
    lastSeenEndDateTime?: string;
    orderTarget?: string;
    orderType?: string;
}

export interface AccountItem {
    uuid: string;
    name: string;
    status: string;
    primaryEmail: string;
    language: string;
    funnel: string;
    photoUrl: string;
    createdAt: string;
    updatedAt: string;
    activatedAt: string | null;
    lastSeen: string | null;
    timezone: string;
}

export interface AccountEmail {
    accountDetailUuid: string;
    status: string;
    isPrimary: boolean;
    email: string;
    name: string;
    createdAt: string | null;
    updatedAt: string | null;
}

export interface AccountMembership {
    rankId: number;
    rankName: string;
    accountDetailUuid: string;
    email: string;
    memberId: number;
    status: string;
    teamId: number;
    name: string;
}

export interface AccountDevicePushToken {
    service: string;
    token: string | null;
    valid_transmission: boolean;
    created_at: string | null;
    updated_at: string | null;
}

// Field names are snake_case because the backend (gardener-service DeviceObject) serializes
// them that way — same convention as the Push History payload.
export interface AccountDevice {
    id: string;
    uuid: string;
    app_version: string;
    model: string;
    name: string;
    platform: string;
    platform_version: string;
    status: string;
    subscribe: boolean;
    created_at: string | null;
    updated_at: string | null;
    // Typed optional/nullable defensively: don't assume the payload includes it — callers should
    // normalize (e.g. `device.push_tokens ?? []`) rather than accessing it as a guaranteed array.
    push_tokens?: AccountDevicePushToken[] | null;
}

export interface AccountDetailData {
    uuid: string;
    name: string;
    lang: string;
    timezone: string;
    utcTimeOffset: string;
    photoUrl: string;
    status: string;
    primaryEmail: string;
    funnel: string;
    objectId: string;
    createdAt: string | null;
    updatedAt: string | null;
    accessedAt: string | null;
    activatedAt: string | null;
    loggedAt: string | null;
    lastSeen: string | null;
    emails: AccountEmail[];
    memberships: AccountMembership[];
    tags: string[];
    devices: AccountDevice[];
}

export interface AccountDailyUsage {
    date: string;
    text: number;
    post: number;
    comment: number;
    sticker: number;
    jandiFileCount: number;
    albumFileCount: number;
    driveFileCount: number;
    connectCount: number;
    messageTotalCount: number;
    fileTotalCount: number;
    fileTotalSize: number;
}

export interface AccountState {
    accounts: AccountItem[];
    totalCount: number;
    page: number;
    perPage: number;
    loading: boolean;
    detail: AccountDetailData | null;
    detailLoading: boolean;
    usages: AccountDailyUsage[];
    usageLoading: boolean;
}

export const initialState: AccountState = {
    accounts: [],
    totalCount: 0,
    page: 1,
    perPage: 50,
    loading: false,
    detail: null,
    detailLoading: false,
    usages: [],
    usageLoading: false,
};

export const searchAccounts = createAsyncThunk<
    { list: AccountItem[]; totalCount: number; page: number; perPage: number },
    AccountSearchParams,
    { state: RootState }
>(
    'account/search',
    async (params, { rejectWithValue }) => {
        try {
            const queryParams = new URLSearchParams();
            Object.entries(params).forEach(([key, value]) => {
                if (value !== undefined && value !== null && value !== '') {
                    if (Array.isArray(value)) {
                        value.forEach(v => queryParams.append(key, String(v)));
                    } else {
                        queryParams.append(key, String(value));
                    }
                }
            });

            const response = await apiClient.get(`/api/account/search?${queryParams.toString()}`);
            return response.data;
        } catch (e: any) {
            return rejectWithValue(e?.response?.data?.message || 'Failed to search accounts');
        }
    },
    {
        condition: (_, { getState }) => {
            const { loading } = (getState() as RootState).account as AccountState;
            return !loading;
        }
    }
);

export const getAccountDetail = createAsyncThunk<AccountDetailData, string, { state: RootState }>(
    'account/detail',
    async (uuid, { rejectWithValue }) => {
        try {
            const response = await apiClient.get(`/api/account/${uuid}`);
            return response.data;
        } catch (e: any) {
            return rejectWithValue(e?.response?.data?.message || 'Failed to get account detail');
        }
    }
);

export const getAccountUsage = createAsyncThunk<
    AccountDailyUsage[],
    { uuid: string; startAt: string; endAt: string; offsetTime?: string },
    { state: RootState }
>(
    'account/usage',
    async (params, { rejectWithValue }) => {
        try {
            const { uuid, startAt, endAt, offsetTime = '+09:00' } = params;
            const response = await apiClient.get(
                `/api/account/${uuid}/usage?startAt=${startAt}&endAt=${endAt}&offsetTime=${encodeURIComponent(offsetTime)}`
            );
            return response.data;
        } catch (e: any) {
            return rejectWithValue(e?.response?.data?.message || 'Failed to get usage data');
        }
    }
);

const accountSlice = createSlice({
    name: 'account',
    initialState,
    extraReducers: (builder) => {
        builder
            .addCase(searchAccounts.pending, (state) => {
                state.loading = true;
            })
            .addCase(searchAccounts.fulfilled, (state, action) => {
                state.loading = false;
                const incomingPage = action.payload.page;
                if (incomingPage === 1) {
                    state.accounts = action.payload.list;
                } else if (incomingPage === state.page + 1) {
                    state.accounts = [...state.accounts, ...action.payload.list];
                } else {
                    return;
                }
                state.totalCount = action.payload.totalCount;
                state.page = incomingPage;
                state.perPage = action.payload.perPage;
            })
            .addCase(searchAccounts.rejected, (state) => {
                state.loading = false;
            })
            .addCase(getAccountDetail.pending, (state) => {
                state.detailLoading = true;
            })
            .addCase(getAccountDetail.fulfilled, (state, action) => {
                state.detailLoading = false;
                state.detail = action.payload;
            })
            .addCase(getAccountDetail.rejected, (state) => {
                state.detailLoading = false;
            })
            .addCase(getAccountUsage.pending, (state) => {
                state.usageLoading = true;
            })
            .addCase(getAccountUsage.fulfilled, (state, action) => {
                state.usageLoading = false;
                state.usages = action.payload;
            })
            .addCase(getAccountUsage.rejected, (state) => {
                state.usageLoading = false;
            });
    },
    reducers: {
        resetAccount: () => initialState,
        clearAccountDetail: (state) => {
            state.detail = null;
            state.usages = [];
        },
        updateDetailTags: (state, action) => {
            if (state.detail) state.detail.tags = action.payload;
        },
    }
});

export const { resetAccount, clearAccountDetail, updateDetailTags } = accountSlice.actions;
export default accountSlice.reducer;
